/**
 * Testimonials Section - Clean social proof cards
 */

import { Star, Quote } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import EditableElement from '../admin/EditableElement';

const defaultTestimonials = [
  {
    id: 1,
    name: 'Sarah M.',
    role: 'Home Renovator',
    quote: 'I saved over $300 on my living room furniture thanks to RUMIO price alerts. The app notified me the moment my dream sofa went on sale!',
    rating: 5,
  },
  {
    id: 2,
    name: 'Michael T.',
    role: 'Apartment Dweller',
    quote: 'The stock alerts are a game changer. I finally got the KALLAX shelf that was out of stock for months. RUMIO pinged me right when it came back!',
    rating: 5,
  },
  {
    id: 3,
    name: 'Emma L.',
    role: 'Interior Designer',
    quote: 'As a designer, I track dozens of products for clients. The browser extension makes it effortless. Highly recommend to any IKEA enthusiast!',
    rating: 5,
  },
];

export default function Testimonials() {
  const content = useWebsiteContentOptional();

  const get = (key: string, fallback: string) =>
    content?.getContent('testimonials', key, fallback) ?? fallback;

  return (
    <section className="py-20 bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <EditableElement section="testimonials" field="badge">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/20 rounded-full text-sm font-medium text-accent-dark mb-4">
              <Star className="w-4 h-4 fill-accent text-accent" />
              {get('badge', 'Testimonials')}
            </div>
          </EditableElement>
          <EditableElement section="testimonials" field="title">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              {get('title', 'Loved by')}
              <span className="text-primary-600">
                {' '}{get('title_highlight', 'smart shoppers')}
              </span>
            </h2>
          </EditableElement>
          <EditableElement section="testimonials" field="subtitle">
            <p className="mt-4 text-lg text-gray-600">
              {get('subtitle', 'Join thousands of happy users who save money on their IKEA purchases.')}
            </p>
          </EditableElement>
        </div>

        {/* Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {defaultTestimonials.map((testimonial) => {
            const name = get(`testimonial${testimonial.id}_name`, testimonial.name);
            const role = get(`testimonial${testimonial.id}_role`, testimonial.role);
            const quote = get(`testimonial${testimonial.id}_quote`, testimonial.quote);

            return (
              <EditableElement key={testimonial.id} section="testimonials" field={`testimonial${testimonial.id}_quote`}>
                <div className="relative bg-white rounded-2xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  {/* Quote icon */}
                  <Quote className="w-10 h-10 mb-4 text-primary-200" />

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                    ))}
                  </div>

                  {/* Quote text */}
                  <p className="text-gray-700 leading-relaxed mb-6">
                    "{quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg bg-primary-600">
                      {name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{name}</p>
                      <p className="text-sm text-gray-500">{role}</p>
                    </div>
                  </div>
                </div>
              </EditableElement>
            );
          })}
        </div>
      </div>
    </section>
  );
}
